/**
 * Convert a number 0-999 to words (American English).
 */
function numberToWordsUnder1000(n: number): string {
  if (n === 0) return "zero";

  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
  ];
  const teens = [
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  if (n < 10) return ones[n];
  if (n < 20) return teens[n - 10];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const o = n % 10;
    return o === 0 ? tens[t] : `${tens[t]}-${ones[o]}`;
  }
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0 ? `${ones[h]} hundred` : `${ones[h]} hundred ${numberToWordsUnder1000(rest)}`;
}

const ILLION_NAMES: Record<number, string> = {
  6: "million",
  9: "billion",
  12: "trillion",
  15: "quadrillion",
  18: "quintillion",
  21: "sextillion",
  24: "septillion",
  27: "octillion",
  30: "nonillion",
  33: "decillion",
  36: "undecillion",
  39: "duodecillion",
  42: "tredecillion",
  45: "quattuordecillion",
  48: "quindecillion",
  51: "sexdecillion",
  54: "septendecillion",
  57: "octodecillion",
  60: "novemdecillion",
  63: "vigintillion",
};

/**
 * Convert a large number in scientific notation to words.
 * e.g. "3.60e+23" -> "three hundred sixty sextillion"
 * Falls back to scientific notation for exponents beyond our word list.
 */
export function formatLargeNumberInWords(scientificNotation: string): string {
  const match = scientificNotation.match(/^([\d.]+)e([+-])(\d+)$/i);
  if (!match) return scientificNotation;

  const mantissa = parseFloat(match[1]);
  const exponent = parseInt(match[3], 10) * (match[2] === "+" ? 1 : -1);

  if (exponent < 6) return scientificNotation;

  const illionExponent = Math.floor(exponent / 3) * 3;
  const illionName = ILLION_NAMES[illionExponent];

  if (!illionName) {
    return scientificNotation;
  }

  const remainder = exponent - illionExponent;
  const coefficient = mantissa * Math.pow(10, remainder);
  const rounded = Math.round(coefficient);
  const isWholeNumber = Math.abs(coefficient - rounded) < 0.01;

  const coefficientWords =
    isWholeNumber && rounded < 1000 && rounded > 0
      ? numberToWordsUnder1000(rounded)
      : coefficient.toLocaleString(undefined, {
          maximumFractionDigits: 1,
          minimumFractionDigits: 0,
        });

  return `${coefficientWords} ${illionName}`;
}

/**
 * Express a large number in familiar terms (billions, trillions) for comprehension.
 * e.g. "3.60e+23" -> "that's 360,000,000 trillion" or "that's 360 billion trillion"
 */
export function formatLargeNumberBreakdown(scientificNotation: string): string {
  const match = scientificNotation.match(/^([\d.]+)e([+-])(\d+)$/i);
  if (!match) return "";

  const mantissa = parseFloat(match[1]);
  const exponent = parseInt(match[3], 10) * (match[2] === "+" ? 1 : -1);

  if (exponent < 6) return "";

  const value = mantissa * Math.pow(10, exponent);

  if (value >= 1e12) {
    const trillions = value / 1e12;
    const formatted = trillions >= 1e9
      ? `${(trillions / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })} billion trillion`
      : trillions >= 1e6
        ? `${(trillions / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })} million trillion`
        : trillions >= 1e3
          ? `${(trillions / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })} thousand trillion`
          : `${trillions.toLocaleString(undefined, { maximumFractionDigits: 0 })} trillion`;
    return `that's ${formatted}!`;
  }
  if (value >= 1e9) {
    const billions = value / 1e9;
    const formatted = billions >= 1e6
      ? `${(billions / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })} million billion`
      : billions >= 1e3
        ? `${(billions / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })} thousand billion`
        : `${billions.toLocaleString(undefined, { maximumFractionDigits: 0 })} billion`;
    return `that's ${formatted}!`;
  }
  if (value >= 1e6) {
    const millions = value / 1e6;
    const formatted = `${millions.toLocaleString(undefined, { maximumFractionDigits: 0 })} million`;
    return `that's ${formatted}!`;
  }
  return "";
}
